import type { VoyageReportKind } from './schemas.js';

/**
 * Sefer raporlarının ortak bölümleri (ADR 0057 §3): hangi türde hangi bölüm gösterilir ve
 * `data`'da hangi isteğe bağlı alan kabul edilir. Alanlar isteğe bağlıdır; eski raporlar ve
 * içerik hash'leri değişmez. Bölüm listeleri ortağın raporlarından (`departure-report.jpeg`, `g2`,
 * `arrival-report.jpeg`, `g4`, `g5`): Departure ve Berthing römorkör, kişi ve gemi durumu; kalkış,
 * COSP, EOSP, demir, drift ve geçiş raporları ROB (yakıt, yağ, tatlı su); hava kalkış, COSP, EOSP,
 * varış, demir ve drift başlangıcında. Noon'un kendi yakıt, yağ, tatlı su ve hava blokları vardır.
 */
export const REPORT_SECTION_KEYS = [
  'tugs',
  'rob',
  'lubes',
  'freshWater',
  'weather',
  'persons',
  'shipCondition',
  'nextOperation',
  'attachments',
  // Kalkış ve varış (ADR 0058 §2, §6): yük, NOR, halat, liman araması bağı
  'cargo',
  'nor',
  'mooring',
  'portCall',
  // Seferin günlükleri (ADR 0059 §3): konum durumu, ETD, yük ilerlemesi, çalışma saatleri
  'daily',
] as const;
export type ReportSection = (typeof REPORT_SECTION_KEYS)[number];

const ROB: readonly ReportSection[] = ['rob', 'lubes', 'freshWater'];
const FLOW: readonly ReportSection[] = ['nextOperation', 'attachments'];
const DAILY: readonly ReportSection[] = ['daily', ...ROB, 'weather', ...FLOW];

export const REPORT_SECTIONS: Readonly<Record<VoyageReportKind, readonly ReportSection[]>> = {
  departure: ['tugs', ...ROB, 'weather', 'persons', 'shipCondition', 'cargo', 'portCall', ...FLOW],
  cosp: [...ROB, 'weather', ...FLOW],
  noon: FLOW,
  eosp: [...ROB, 'weather', 'nor', ...FLOW],
  arrival: [
    'tugs',
    ...ROB,
    'weather',
    'persons',
    'shipCondition',
    'nor',
    'mooring',
    'cargo',
    'portCall',
    ...FLOW,
  ],
  anchorage_start: [...ROB, 'weather', 'nor', ...FLOW],
  port_daily: DAILY,
  anchorage_daily: DAILY,
  anchorage_end: [...ROB, ...FLOW],
  waiting_start: ['weather', ...FLOW],
  waiting_end: FLOW,
  drift_start: [...ROB, 'weather', ...FLOW],
  drift_daily: DAILY,
  drift_end: [...ROB, ...FLOW],
  strait_entry: [...ROB, ...FLOW],
  strait_exit: [...ROB, ...FLOW],
  canal_entry: [...ROB, ...FLOW],
  canal_exit: [...ROB, ...FLOW],
  cargo_start: ['attachments'],
  cargo_complete: ['attachments'],
  on_hire: ['attachments'],
  off_hire: ['attachments'],
};

export function reportHasSection(kind: VoyageReportKind, section: ReportSection): boolean {
  return REPORT_SECTIONS[kind]?.includes(section) ?? false;
}
